import { ActionBarPrimitive, AuiIf } from '@assistant-ui/react';
import { IconCheck, IconCopy, IconDownload, IconFileZip, IconRefresh } from '@tabler/icons-react';
import { use } from 'react';

import { downloadCsvZip } from '#core/lib/transforms/download-csv-zip.ts';

import { TooltipIconButton } from '../tooltip-icon-button/TooltipIconButton.tsx';
import classes from './AssistantActionBar.module.css';
import { MessageExportContext } from '#plugin-chat/contexts/MessageExportContext.ts';

/** Action bar for assistant messages with copy, export, and regeneration controls. */
export function AssistantActionBar() {
  // Read the per-message export registry the enclosing `<AssistantMessage>`
  // provides. The "download all data" button only appears once at least
  // one rendered dashboard has at least one CSV-producing tile — i.e. the
  // current message actually carries data worth bundling.
  const messageExport = use(MessageExportContext);
  const dashboardCount = messageExport?.entries.length ?? 0;
  const downloadAll = () => {
    if (messageExport === null) {
      return;
    }
    // Namespace each tile's CSV by its dashboard's name so the resulting ZIP
    // has a folder structure (`<dashboard>/<tile>.csv`); the buildCsvZip
    // dedup pass inside `downloadCsvZip` handles colliding paths.
    downloadCsvZip(
      messageExport.entries.flatMap((dashboard) =>
        dashboard.getEntries().map((tile) => ({ name: `${dashboard.name}/${tile.name}`, csv: tile.getCsv() })),
      ),
      'message-data',
    );
  };
  return (
    <ActionBarPrimitive.Root
      autohide="not-last"
      autohideFloat="single-branch"
      className={classes.assistantActionBar}
      hideWhenRunning
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          {/* Show checkmark briefly after successful copy */}
          <AuiIf condition={({ message }) => message.isCopied}>
            <IconCheck />
          </AuiIf>
          <AuiIf condition={({ message }) => !message.isCopied}>
            <IconCopy />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.ExportMarkdown asChild>
        <TooltipIconButton tooltip="Export as Markdown">
          <IconDownload />
        </TooltipIconButton>
      </ActionBarPrimitive.ExportMarkdown>
      {dashboardCount > 0 && (
        <TooltipIconButton onClick={downloadAll} tooltip="Download all dashboard data">
          <IconFileZip />
        </TooltipIconButton>
      )}
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Refresh">
          <IconRefresh />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
}
